/**
 * Pure display-math for the Analytics page — extracted so every number the UI
 * renders (deltas, funnel %, donut %, inbox rate, bar heights, CSV) is
 * unit-testable (analyticsMath.test.ts). No React, no DOM.
 */

/** Period-over-period change %. null when there's no prior baseline and no current value. */
export function deltaPct(cur: number, prev: number): number | null {
  return prev > 0 ? Math.round(((cur - prev) / prev) * 100) : (cur > 0 ? 100 : null);
}

/** Funnel step as a % of the step above it. null when the denominator is 0 (renders blank). */
export function funnelPct(part: number, whole: number): number | null {
  return whole ? Math.round((100 * part) / whole) : null;
}

/** One slice's share of a donut total (guards divide-by-zero). */
export function donutPct(value: number, total: number): number {
  return Math.round((value / (total || 1)) * 100);
}

/** % of sent email that reached the inbox = delivered% minus bounced%, floored at 0. */
export function inboxRate(deliveryRate: number, bounceRate: number): number {
  return Math.max(0, deliveryRate - bounceRate);
}

/** Bar height as a % of the chart max; a non-zero value never renders thinner than `min`. */
export function barHeightPct(value: number, max: number, min = 2): number {
  const v = value || 0;
  return Math.max(v ? min : 0, (v / (max || 1)) * 100);
}

/** Cumulative donut segments [{ from, to, pct }] as percentages of the total. */
export function donutSegments<T extends { value: number }>(data: T[]): (T & { from: number; to: number; pct: number })[] {
  const total = data.reduce((s, d) => s + d.value, 0);
  let acc = 0;
  return data.map((d) => {
    const from = total === 0 ? 0 : (acc / total) * 100;
    acc += d.value;
    const to = total === 0 ? 0 : (acc / total) * 100;
    return { ...d, from, to, pct: donutPct(d.value, total) };
  });
}

/** ISO-3166 alpha-2 → flag emoji (two regional-indicator symbols). */
export function flagEmoji(cc: string): string {
  if (!/^[A-Za-z]{2}$/.test(cc)) return '🏳️';
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** Compact "how long ago" label (s/m/h/d). `now` is injectable for tests. */
export function relTime(ms: number, now: number = Date.now()): string {
  if (!ms) return '';
  const s = Math.max(1, Math.floor((now - ms) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** RFC-4180 CSV cell escaping. */
export function csvCell(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Build a full CSV string (with BOM + CRLF) from a header row and data rows. */
export function toCsv(headers: (string | number)[], rows: (string | number)[][]): string {
  const line = (r: (string | number)[]) => r.map(csvCell).join(',');
  return '﻿' + [headers, ...rows].map(line).join('\r\n');
}
